import { withoutKeys } from "./configurationPatch";
import { requestedOAuthScopes } from "./oauthScopes";
import { connectorsByIdentifier } from "./index";
import type { ConnectorOAuthConfig } from "./types";

type DcrOAuthConfig = Extract<
  ConnectorOAuthConfig,
  { clientCredentialsFrom: "dcr" }
>;

export function isValidDcrEndpoint(
  value: string | undefined,
  serverUrl: string
) {
  const protocol = URL.parse(value ?? "")?.protocol;
  return (
    protocol === "https:" ||
    (protocol === "http:" && URL.parse(serverUrl)?.protocol === "http:")
  );
}

export function hasDcrConfiguration(
  oauth: ConnectorOAuthConfig,
  configuration: Record<string, string>,
  clientId: string | null | undefined
): boolean {
  return (
    oauth.clientCredentialsFrom === "dcr" &&
    !!clientId &&
    isValidDcrEndpoint(
      oauth.authorizationEndpoint?.(configuration),
      oauth.discoverFrom(configuration)
    ) &&
    isValidDcrEndpoint(
      oauth.tokenEndpoint(configuration),
      oauth.discoverFrom(configuration)
    )
  );
}

export function dcrClientPending(
  oauth: ConnectorOAuthConfig | undefined,
  configuration: Record<string, string>,
  clientId: string | null | undefined
) {
  return (
    oauth?.clientCredentialsFrom === "dcr" &&
    !hasDcrConfiguration(oauth, configuration, clientId)
  );
}

type DcrScopeInputs = {
  configuration: Record<string, string>;
  oauthRequestedScopes: string[] | null;
};

function scopeKey(scopes: string[] | null) {
  return JSON.stringify([...new Set(scopes ?? [])].sort());
}

function scopesChanged(
  oauth: DcrOAuthConfig,
  before: DcrScopeInputs,
  after: DcrScopeInputs
) {
  const key = (input: DcrScopeInputs) =>
    scopeKey(
      requestedOAuthScopes(
        oauth,
        input.configuration,
        input.oauthRequestedScopes
      )
    );
  return key(before) !== key(after);
}

export function dcrInputsChanged(
  oauth: DcrOAuthConfig,
  before: DcrScopeInputs,
  after: DcrScopeInputs
) {
  return (
    oauth.discoverFrom(before.configuration) !==
      oauth.discoverFrom(after.configuration) ||
    before.configuration.authMode !== after.configuration.authMode ||
    before.configuration.caCertificate !== after.configuration.caCertificate ||
    scopeKey(before.oauthRequestedScopes) !==
      scopeKey(after.oauthRequestedScopes)
  );
}

export function dcrRegistrationChanged(
  before: DcrScopeInputs & { identifier: string },
  after: DcrScopeInputs & { identifier: string }
) {
  const oauth = connectorsByIdentifier[after.identifier]?.oauth;
  return (
    before.identifier === after.identifier &&
    oauth?.clientCredentialsFrom === "dcr" &&
    (oauth.discoverFrom(before.configuration) !==
      oauth.discoverFrom(after.configuration) ||
      scopesChanged(oauth, before, after))
  );
}

const DISCOVERED_CONFIGURATION_KEYS = [
  "authorizeUrl",
  "tokenUrl",
  "issuer",
  "resource",
  "discoveredScopes",
];

export function withoutDiscovery(configuration: Record<string, string>) {
  return withoutKeys(configuration, DISCOVERED_CONFIGURATION_KEYS);
}
